/**
 * Where a story happens ([Stories](../../../docs/STORIES.md)).
 *
 * A banner above the conversation: the scene, with Sneakers and the guest
 * standing on it. Both halves are generated rather than commissioned
 * ([D-113](../../../docs/DECISIONS.md#d-113)) — twelve stages covering the
 * settings the hundred chapters visit, and the four guests the stories were
 * always written for.
 *
 * **Decoration, and it says so.** The scene carries an `aria-label` naming the
 * place, because "at the café" is information a child using VoiceOver should
 * have; the characters on it are `aria-hidden`, because every line below is
 * already labelled with who said it and hearing "Pip, Pip, Pip" before each one
 * is noise ([Accessibility](../../../docs/ACCESSIBILITY.md)).
 *
 * **Nothing here is load-bearing.** A `sceneRef` that names no stage, or a
 * speaker who is nobody, draws nothing and the story runs exactly as it did
 * before any of this existed.
 */

import sneakers from '@capytalk/tokens/sneakers.svg';
import { type Drawn, guest, scene } from '@capytalk/tokens';

/** The inline markup is generated, CI-checked and ours — never user input. */
const Inline = ({
  art,
  height,
  label,
  style,
}: {
  readonly art: Drawn;
  readonly height: number | string;
  readonly label: string | null;
  readonly style?: React.CSSProperties;
}) => (
  <svg
    viewBox={art.viewBox}
    height={height}
    {...(label === null
      ? { 'aria-hidden': true }
      : { role: 'img', 'aria-label': label })}
    style={style}
    dangerouslySetInnerHTML={{ __html: art.body }}
  />
);

/**
 * The guest's face, small, for the corner of a speech bubble.
 *
 * `uid` is the exercise: every portrait of one guest within a story may share
 * gradient ids, and they must not be shared with a *different* guest — which is
 * what the id is keyed on.
 */
export function Portrait({ speaker, size = 26 }: { speaker: string; size?: number }) {
  if (speaker === 'sneakers') {
    return <img src={sneakers} alt="" width={size} height={size} aria-hidden />;
  }
  const who = guest(speaker, speaker);
  if (who === undefined) return null;
  return <Inline art={who} height={size} label={null} />;
}

export default function Stage({
  sceneRef,
  cast,
}: {
  readonly sceneRef: string | undefined;
  /** Who is in it. Sneakers always plays himself, so the guest is the rest. */
  readonly cast: readonly string[];
}) {
  const where = scene(sceneRef);
  if (where === undefined) return null;

  const other = cast.find((who) => who !== 'sneakers') ?? null;
  const them = other === null ? undefined : guest(other, other);

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        marginBottom: 10,
        // Its own aspect ratio, so the characters keep their footing at any
        // width. A fixed height would strand them mid-air on a wide phone.
        aspectRatio: '320 / 120',
      }}
    >
      <Inline
        art={where}
        height="100%"
        label={where.name}
        style={{ width: '100%', display: 'block' }}
      />
      {/*
        Standing on the floor, which the stages keep clear at these two spots
        ([`tools/art/scenes.py`](../../../tools/art/scenes.py)): props go to the
        edges or high up, so nobody is drawn behind a table.
      */}
      <img
        src={sneakers}
        alt=""
        aria-hidden
        style={{ position: 'absolute', left: '14%', bottom: '10%', height: '70%' }}
      />
      {them !== undefined && (
        <Inline
          art={them}
          height="70%"
          label={null}
          style={{ position: 'absolute', right: '14%', bottom: '10%' }}
        />
      )}
    </div>
  );
}
